import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@work4all/data';
import {
  FileViewMode,
  useWidgetsDataBag,
} from '@work4all/data/lib/hooks/useWidgetsBag';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ILayoutGroupDefinition } from '@work4all/models/lib/Layout';

import { useCanFilterEntitiesByPermission } from '../../../../../../components/data-tables/offers-table/hooks/use-can-filter-entities-by-permission';
import { ENTITY_BY_CONVERT_CONFIGS } from '../../../../../../components/data-tables/offers-table/hooks/use-clone-convert-erp';
import { WidgetDragStateProvider } from '../hooks/use-widget-drag-state';
import { WidgetGroup } from '../WidgetGroup';

const ENTITIES_TO_CHECK_CONVERT_PERMISSION = [
  Entities.calculation,
  Entities.offer,
  Entities.contract,
  Entities.invoice,
  Entities.deliveryNote,
  Entities.demand,
  Entities.order,
  Entities.inboundDeliveryNote,
];

export const WidgetsController = memo(function WidgetsController(props: {
  viewMode: FileViewMode;
}) {
  const { widgetsDefinitions } = useWidgetsDataBag();
  const { t } = useTranslation();

  const { viewMode } = props;

  function getGroupDisplayName(group: ILayoutGroupDefinition): string {
    if (group.type === 'default' && group.i18nKey) {
      return t(group.i18nKey);
    } else {
      return group.name;
    }
  }

  const widgetGroups = widgetsDefinitions.definition;

  const { filterEntitiesByPermission } = useCanFilterEntitiesByPermission();

  const { canConvert } = usePermissions();

  const convertibleEntities = useMemo(() => {
    return ENTITIES_TO_CHECK_CONVERT_PERMISSION.filter((entity) => {
      const __types = ENTITY_BY_CONVERT_CONFIGS[entity]?.allowedEntities ?? [];

      return (
        canConvert({ entity }) ||
        __types.filter(filterEntitiesByPermission).length > 0
      );
    });
  }, [canConvert, filterEntitiesByPermission]);

  /**
   * For rendering issues the view has to be reset when hiddenWidgets is changed
   */

  if (!widgetsDefinitions) {
    return null;
  }

  switch (widgetGroups.length) {
    case 0:
      return null;

    default:
      return (
        <WidgetDragStateProvider>
          {widgetGroups.map((group) => (
            <WidgetGroup
              key={group.id}
              id={group.id}
              name={getGroupDisplayName(group)}
              defaultCollapsed={group.collapsed}
              widgets={group.widgets}
              viewMode={viewMode}
              convertibleEntities={convertibleEntities}
            />
          ))}
        </WidgetDragStateProvider>
      );
  }
});
